<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('createSubscription.subtitle') }} {{ $t('createSubscription.requiredFields') }}</span>
      </div>
    </div>

    <!-- ===== 1. TYPE D'ABONNEMENT ===== -->
    <div class="ov-card mb-6">
      <div class="ov-card__head">
        <div class="ov-card__title"><span class="cs-num">1</span> {{ $t('createSubscription.selectType') }}</div>
      </div>
      <div class="ov-card__body">
        <div v-if="referenceAbonnementList.length === 0" class="ov-empty">Aucun type d'abonnement disponible.</div>
        <div v-else class="cs-types">
          <button
            v-for="abonnement in referenceAbonnementList"
            :key="abonnement.designation"
            type="button"
            class="cs-type"
            :class="{ 'cs-type--selected': typeAbonnement === abonnement.designation }"
            @click="selectAbonnement(abonnement.designation)"
          >
            <span class="cs-radio"></span>
            <span>
              <span class="cs-type__title">{{ typeLabel(abonnement.designation) }}</span>
              <span class="cs-type__text">{{ typeDescription(abonnement.designation) }}</span>
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- ===== 2. PARAMÈTRES ===== -->
    <div v-if="typeAbonnement" class="ov-grid">
      <form class="ov-card" novalidate @submit.prevent="submitForm">
        <div class="ov-card__head">
          <div class="ov-card__title"><span class="cs-num">2</span> {{ $t('createSubscription.addSubscription') }}</div>
          <span class="ov-badge ov-badge--navy ov-badge--plain">{{ typeLabel(typeAbonnement) }}</span>
        </div>

        <div class="ov-card__body">
          <div class="ov-row">
            <div v-if="isAdminSocieteMere" class="ov-field ov-col-full">
              <label class="ov-label" for="cs-company">{{ $t('createSubscription.transportCompany') }}<span class="ov-required">*</span></label>
              <select id="cs-company" v-model="compagnieTransportRaisonSociale" class="ov-select" :class="{ 'is-invalid': submitted && errors.company }">
                <option :value="null" disabled>Sélectionnez une compagnie</option>
                <option v-for="(compagnie, ct) in compagnieTransportList" :key="ct" :value="compagnie.raisonSociale">{{ compagnie.raisonSociale }}</option>
              </select>
              <div v-if="submitted && errors.company" class="ov-error">{{ errors.company }}</div>
            </div>

            <div class="ov-field ov-col-full">
              <label class="ov-label" for="cs-designation">{{ $t('createSubscription.designation') }}<span class="ov-required">*</span></label>
              <input
                v-if="isPeriodique"
                id="cs-designation"
                v-model.trim="modeAbonnementPeriodiqueModel.designation"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': submitted && errors.designation }"
                placeholder="Ex : Abonnement mensuel standard"
              />
              <input
                v-else
                id="cs-designation"
                v-model.trim="modeAbonnementPrelevementModel.designation"
                type="text"
                class="ov-input"
                :class="{ 'is-invalid': submitted && errors.designation }"
                placeholder="Ex : Prélèvement sur ventes"
              />
              <div v-if="submitted && errors.designation" class="ov-error">{{ errors.designation }}</div>
            </div>

            <!-- Périodique : redevances -->
            <template v-if="isPeriodique">
              <div class="ov-field">
                <label class="ov-label" for="cs-redevance">{{ $t('createSubscription.fee') }}<span class="ov-required">*</span></label>
                <div class="ov-input-suffix">
                  <input id="cs-redevance" v-model.number="modeAbonnementPeriodiqueModel.redevance" type="number" min="0" class="ov-input" :class="{ 'is-invalid': submitted && errors.redevance }" placeholder="0" />
                  <span>FCFA</span>
                </div>
                <div v-if="submitted && errors.redevance" class="ov-error">{{ errors.redevance }}</div>
              </div>
              <div class="ov-field">
                <label class="ov-label" for="cs-pub">{{ $t('createSubscription.advertisingFee') }}<span class="ov-required">*</span></label>
                <div class="ov-input-suffix">
                  <input id="cs-pub" v-model.number="modeAbonnementPeriodiqueModel.redevancePublicite" type="number" min="0" class="ov-input" :class="{ 'is-invalid': submitted && errors.redevancePublicite }" placeholder="0" />
                  <span>FCFA</span>
                </div>
                <div v-if="submitted && errors.redevancePublicite" class="ov-error">{{ errors.redevancePublicite }}</div>
              </div>
            </template>

            <!-- Prélèvement : taux -->
            <div v-else class="ov-field">
              <label class="ov-label" for="cs-taux">{{ $t('createSubscription.rate') }}<span class="ov-required">*</span></label>
              <div class="ov-input-suffix">
                <input id="cs-taux" v-model.number="modeAbonnementPrelevementModel.taux" type="number" min="0" max="100" step="0.1" class="ov-input" :class="{ 'is-invalid': submitted && errors.taux }" placeholder="0" />
                <span>%</span>
              </div>
              <div v-if="submitted && errors.taux" class="ov-error">{{ errors.taux }}</div>
            </div>

            <div class="ov-field" :class="{ 'ov-col-full': isPeriodique }">
              <label class="ov-label" for="cs-periodicite">{{ $t('createSubscription.periodicity') }}<span class="ov-required">*</span></label>
              <select
                v-if="isPeriodique"
                id="cs-periodicite"
                v-model="modeAbonnementPeriodiqueModel.periodiciteAbonnementDesignation"
                class="ov-select"
                :class="{ 'is-invalid': submitted && errors.periodicite }"
              >
                <option :value="null" disabled>Sélectionnez une périodicité</option>
                <option v-for="(periodicite, p) in referencePeriodiciteList" :key="p" :value="periodicite.designation">{{ periodicite.designation }}</option>
              </select>
              <select
                v-else
                id="cs-periodicite"
                v-model="modeAbonnementPrelevementModel.periodiciteAbonnementDesignation"
                class="ov-select"
                :class="{ 'is-invalid': submitted && errors.periodicite }"
              >
                <option :value="null" disabled>Sélectionnez une périodicité</option>
                <option v-for="(periodicite, p) in referencePeriodiciteList" :key="p" :value="periodicite.designation">{{ periodicite.designation }}</option>
              </select>
              <div v-if="submitted && errors.periodicite" class="ov-error">{{ errors.periodicite }}</div>
            </div>

            <div class="ov-field">
              <label class="ov-label" for="cs-debut">{{ $t('createSubscription.startDate') }}<span class="ov-required">*</span></label>
              <input v-if="isPeriodique" id="cs-debut" v-model="dateDebutAbonnementPeriodique" type="date" class="ov-input" :class="{ 'is-invalid': submitted && errors.debut }" />
              <input v-else id="cs-debut" v-model="dateDebutAbonnementPrevelement" type="date" class="ov-input" :class="{ 'is-invalid': submitted && errors.debut }" />
              <div v-if="submitted && errors.debut" class="ov-error">{{ errors.debut }}</div>
            </div>
            <div class="ov-field">
              <label class="ov-label" for="cs-fin">{{ $t('createSubscription.endDate') }}<span class="ov-required">*</span></label>
              <input v-if="isPeriodique" id="cs-fin" v-model="dateFinAbonnementPeriodique" type="date" class="ov-input" :min="dateDebutAbonnementPeriodique" :class="{ 'is-invalid': submitted && errors.fin }" />
              <input v-else id="cs-fin" v-model="dateFinAbonnementPrevelement" type="date" class="ov-input" :min="dateDebutAbonnementPrevelement" :class="{ 'is-invalid': submitted && errors.fin }" />
              <div v-if="submitted && errors.fin" class="ov-error">{{ errors.fin }}</div>
            </div>
          </div>
        </div>

        <div class="ov-card__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="resetForm">
            {{ $t('createSubscription.reset') }}
          </button>
          <button type="submit" class="ov-btn ov-btn--primary" :disabled="overlay">
            <v-icon small>mdi-content-save-outline</v-icon>
            {{ $t('createSubscription.save') }}
          </button>
        </div>
      </form>

      <!-- ===== RÉCAPITULATIF ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div class="ov-card__title">Récapitulatif</div>
          </div>
          <div class="ov-card__body">
            <div class="ov-stat">
              <span class="ov-stat__label">Type</span>
              <span class="ov-stat__value">{{ typeLabel(typeAbonnement) }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Compagnie</span>
              <span class="ov-stat__value">{{ targetCompany || '—' }}</span>
            </div>
            <template v-if="isPeriodique">
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('createSubscription.fee') }}</span>
                <span class="ov-stat__value">{{ formatAmount(modeAbonnementPeriodiqueModel.redevance) }}</span>
              </div>
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('createSubscription.advertisingFee') }}</span>
                <span class="ov-stat__value">{{ formatAmount(modeAbonnementPeriodiqueModel.redevancePublicite) }}</span>
              </div>
            </template>
            <div v-else class="ov-stat">
              <span class="ov-stat__label">{{ $t('createSubscription.rate') }}</span>
              <span class="ov-stat__value">{{ typeof modeAbonnementPrelevementModel.taux === 'number' ? modeAbonnementPrelevementModel.taux + ' %' : '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">{{ $t('createSubscription.periodicity') }}</span>
              <span class="ov-stat__value">{{ current.model.periodiciteAbonnementDesignation || '—' }}</span>
            </div>
            <div class="ov-stat">
              <span class="ov-stat__label">Période</span>
              <span class="ov-stat__value">
                <template v-if="current.debut && current.fin">{{ formatDate(current.debut) }} → {{ formatDate(current.fin) }}</template>
                <template v-else>—</template>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_CREER_MODE_ABONNEMENT,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";

export default {
  name: "CreerModeAbonnement",
  data() {
    return {
      errorMsg: null,
      successMsg: null,
      overlay: false,
      // Affiche les erreurs sous les champs après une tentative d'enregistrement
      submitted: false,

      dateDebutAbonnementPrevelement: null,
      dateFinAbonnementPrevelement: null,

      compagnieTransportRaisonSociale: null,
      compagnieTransportList: [],
      options: {
        index: 0,
        size: 8,
      },

      dateDebutAbonnementPeriodique: null,
      dateFinAbonnementPeriodique: null,

      referenceAbonnementList: [],
      referencePeriodiciteList: [],

      typeAbonnement: null,

      referenceAbonnement: {
        referenceFamilleDesignation: "referenceFamilleAbonnement",
      },
      referencePeriodiciteAbonnement: {
        referenceFamilleDesignation: "referenceFamillePeriodiciteAbonnement",
      },

      objectToSendReferenceAbonnement: {
        datas: [],
      },
      objectToSendReferencePeriodicite: {
        datas: [],
      },

      modeAbonnementPeriodiqueModel: {
        designation: null,
        dateDebutAbonnement: null,
        dateFinAbonnement: null,
        redevance: null,
        redevancePublicite: null,
        typeModeAbonnementDesignation: null,
        periodiciteAbonnementDesignation: null,
        compagnieTransportRaisonSociale: null,
      },

      modeAbonnementPrelevementModel: {
        designation: null,
        dateDebutAbonnement: null,
        dateFinAbonnement: null,
        taux: null,
        periodiciteAbonnementDesignation: null,
        compagnieTransportRaisonSociale: null,
      },

      abonnementObject: {
        datas: [],
      },
    };
  },

  methods: {
    selectAbonnement(designation) {
      this.typeAbonnement = designation;
      this.submitted = false;
    },

    // Libellé lisible d'un type d'abonnement
    typeLabel(designation) {
      return (
        { AbonnementPeriodique: "Abonnement périodique", AbonnementPrelevement: "Abonnement par prélèvement" }[designation] ||
        designation
      );
    },

    typeDescription(designation) {
      return (
        {
          AbonnementPeriodique: "Une redevance fixe (et une redevance publicité) facturée à chaque période.",
          AbonnementPrelevement: "Un pourcentage prélevé sur les ventes de la compagnie, à chaque période.",
        }[designation] || ""
      );
    },

    resetForm() {
      if (this.isPeriodique) {
        this.reinintializeSubscriptionModePeriod();
      } else {
        this.reinintializeSubscriptionModePrelevment();
      }
      this.submitted = false;
    },

    reinintializeSubscriptionModePeriod() {
      this.modeAbonnementPeriodiqueModel.designation = null;
      this.modeAbonnementPeriodiqueModel.typeModeAbonnementDesignation = null;
      this.modeAbonnementPeriodiqueModel.periodiciteAbonnementDesignation =
        null;
      this.dateDebutAbonnementPeriodique = null;
      this.dateFinAbonnementPeriodique = null;
      this.modeAbonnementPeriodiqueModel.redevance = null;
      this.modeAbonnementPeriodiqueModel.redevancePublicite = null;
      this.compagnieTransportRaisonSociale = null;
    },

    reinintializeSubscriptionModePrelevment() {
      this.modeAbonnementPrelevementModel.designation = null;
      this.modeAbonnementPrelevementModel.periodiciteAbonnementDesignation =
        null;
      this.modeAbonnementPrelevementModel.taux = null;
      this.dateDebutAbonnementPrevelement = null;
      this.dateFinAbonnementPrevelement = null;
      this.compagnieTransportRaisonSociale = null;
    },

    // OBTENIR LA LISTE DES DEMANDES D'ADHESIONS VALIDÉES
    async getCompagnieTransportList() {
      if (
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
      ) {
        axios
          .post(
            API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
            this.options,
            {
              headers: HEADERS(this.$store.state.userAuthentified.token),
            }
          )
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code != 800) {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              } else {
                this.compagnieTransportList = response.data.items;
              }
            } else {
              this.errorMsg = this.$t('createSubscription.errorDataRetrieval');
              showErrorMessage();
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      }
    },

    // API CREATE SUBSCRIPTION MODE
    async apiSubscriptionMode() {
      await axios
        .post(API_CREER_MODE_ABONNEMENT, this.abonnementObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              // La saisie n'est vidée qu'en cas de succès
              this.resetForm();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else {
            this.errorMsg = this.$t('createSubscription.errorCreation');
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || this.$t('createSubscription.errorCreation');
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
          this.abonnementObject.datas = [];
        });
    },

    // CREER UN MODE ABONNEMENT DE TYPE PERIODIQUE
    async creerModeAbonnementPeriodique() {
      this.submitted = true;
      if (!this.formValid || this.overlay) {
        this.errorMsg = this.$t('createSubscription.fillRequiredFields');
        showErrorMessage();
        return;
      }
      const model = Object.assign({}, this.modeAbonnementPeriodiqueModel, {
        dateDebutAbonnement: this.formatDate(this.dateDebutAbonnementPeriodique),
        dateFinAbonnement: this.formatDate(this.dateFinAbonnementPeriodique),
        typeModeAbonnementDesignation: this.typeAbonnement,
        compagnieTransportRaisonSociale: this.targetCompany,
      });
      this.abonnementObject.datas = [model];
      this.overlay = true;
      await this.apiSubscriptionMode();
    },

    // CREER UN MODE ABONNEMENT DE TYPE PRELEVEMENT
    async creerModeAbonnementPrelevement() {
      this.submitted = true;
      if (!this.formValid || this.overlay) {
        this.errorMsg = this.$t('createSubscription.fillRequiredFields');
        showErrorMessage();
        return;
      }
      const model = Object.assign({}, this.modeAbonnementPrelevementModel, {
        dateDebutAbonnement: this.formatDate(this.dateDebutAbonnementPrevelement),
        dateFinAbonnement: this.formatDate(this.dateFinAbonnementPrevelement),
        typeModeAbonnementDesignation: this.typeAbonnement,
        compagnieTransportRaisonSociale: this.targetCompany,
      });
      this.abonnementObject.datas = [model];
      this.overlay = true;
      await this.apiSubscriptionMode();
    },

    submitForm() {
      if (this.isPeriodique) {
        this.creerModeAbonnementPeriodique();
      } else {
        this.creerModeAbonnementPrelevement();
      }
    },

    //OBTENIR REFERENCE DESIGNATION ABONNEMENT
    async obtenirReferenceAbonnementList() {
      this.objectToSendReferenceAbonnement.datas.push(this.referenceAbonnement);
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.objectToSendReferenceAbonnement,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          this.referenceAbonnementList = response.data.items || [];
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    //OBTENIR REFERENCE DESIGNATION PERIODICITÉ
    async obtenirReferencePeriodicteAbonnementList() {
      this.objectToSendReferencePeriodicite.datas.push(
        this.referencePeriodiciteAbonnement
      );
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.objectToSendReferencePeriodicite,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          this.referencePeriodiciteList = response.data.items || [];
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // FORMAT DE LA DATE
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },

  },

  computed: {
    isAdminSocieteMere() {
      return this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere";
    },

    isPeriodique() {
      return this.typeAbonnement == "AbonnementPeriodique";
    },

    // Compagnie concernée : choisie par l'admin société mère, sinon celle de l'utilisateur
    targetCompany() {
      return this.isAdminSocieteMere
        ? this.compagnieTransportRaisonSociale
        : this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
    },

    // Champs et dates du formulaire du type sélectionné
    current() {
      return this.isPeriodique
        ? { model: this.modeAbonnementPeriodiqueModel, debut: this.dateDebutAbonnementPeriodique, fin: this.dateFinAbonnementPeriodique }
        : { model: this.modeAbonnementPrelevementModel, debut: this.dateDebutAbonnementPrevelement, fin: this.dateFinAbonnementPrevelement };
    },

    errors() {
      const m = this.current.model;
      const isAmount = (v) => typeof v === "number" && !isNaN(v) && v >= 0;
      const errors = {
        company: this.isAdminSocieteMere && !this.compagnieTransportRaisonSociale ? "Sélectionnez une compagnie." : null,
        designation: !m.designation ? "La désignation est obligatoire." : null,
        periodicite: !m.periodiciteAbonnementDesignation ? "Sélectionnez une périodicité." : null,
        debut: !this.current.debut ? "La date de début est obligatoire." : null,
        fin: !this.current.fin
          ? "La date de fin est obligatoire."
          : this.current.debut && this.current.fin < this.current.debut
          ? "La date de fin doit être après la date de début."
          : null,
      };
      if (this.isPeriodique) {
        errors.redevance = !isAmount(m.redevance) ? "Indiquez un montant positif." : null;
        errors.redevancePublicite = !isAmount(m.redevancePublicite) ? "Indiquez un montant positif." : null;
      } else {
        errors.taux = !(typeof m.taux === "number" && m.taux > 0 && m.taux <= 100) ? "Le taux doit être compris entre 0 et 100 %." : null;
      }
      return errors;
    },

    formValid() {
      return !!this.targetCompany && Object.values(this.errors).every((e) => !e);
    },

    formatAmount() {
      return (v) => (typeof v === "number" ? v.toLocaleString("fr-FR") + " FCFA" : "—");
    },
  },

  mounted() {
    this.obtenirReferenceAbonnementList();
    this.obtenirReferencePeriodicteAbonnementList();
    this.getCompagnieTransportList();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.cs-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  color: #ffffff;
  background: $ov-green;
}

.cs-types {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.cs-type {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  text-align: left;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(21, 158, 114, 0.45);
  }

  &--selected {
    border-color: $ov-green;
    background: rgba(21, 158, 114, 0.05);
    box-shadow: 0 0 0 3px rgba(21, 158, 114, 0.12);
  }
}

.cs-radio {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.25);

  .cs-type--selected & {
    border-color: $ov-green;
    box-shadow: inset 0 0 0 4px #ffffff, inset 0 0 0 10px $ov-green;
  }
}

.cs-type__title {
  display: block;
  font-size: 15px;
  font-weight: 800;
  color: $ov-navy;
}

.cs-type__text {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: $ov-muted;
}
</style>
